import { Box, Flex, Icon, Tooltip, useDisclosure } from '@chakra-ui/react';
import { KButton, KTag, KText } from '@/ui-library/components';
import { InfoCircle } from '@kula-ai/icons/dual-tone/InfoCircle';
import { KPopover } from '@/ui-library/components/organisms/KPopover/KPopover';
import { Requisition } from '../associateReq.interface';
import { StatusTag } from './RequisitionItem';
import { requisitionTypeConfig } from '../../constants/constants';
import { RequisitionsDetailsDrawer } from '../../components';

interface RequisitionDetailsPopoverProps {
  value: string;
  requisition?: Requisition;
  readOnlyOnMobile?: boolean;
}

export const RequisitionDetailsPopover = ({
  value,
  requisition,
  readOnlyOnMobile,
}: RequisitionDetailsPopoverProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const reqActions = useDisclosure();

  if (!requisition) {
    return null;
  }

  const detailsFields = [
    {
      label: 'Status',
      renderValue: (value: string) => (
        <StatusTag status={requisition?.status} />
      ),
    },
    {
      label: 'Role name',
      value: requisition?.role_name || '-',
    },
    {
      label: 'Target hire date',
      value: requisition?.target_hire_date || '-',
    },
    {
      label: 'Requisition type',
      renderValue: (value: string) => (
        <KText>
          {requisitionTypeConfig[requisition?.requisition_type]?.label}
        </KText>
      ),
    },
  ];

  const requisitionDetails = (
    <Flex flexDir="column" gap="12px" p="4px">
      {detailsFields.map((field, index) => (
        <Flex key={index} flexDir="column" gap="6px">
          <KText color="primary-900">{field.label}</KText>
          {field.renderValue ? (
            field.renderValue(field.value)
          ) : (
            <KText>{field.value}</KText>
          )}
        </Flex>
      ))}
      {requisition?.confidential && (
        <Tooltip
          label="The requisition associated with this job is confidential"
          placement="top"
          hasArrow
        >
          <Box>
            <KTag value="Confidential requisition" width="max-content" />
          </Box>
        </Tooltip>
      )}
      {!requisition?.confidential && (
        <KButton variant="quaternary" onClick={reqActions?.onOpen}>
          View requisition
        </KButton>
      )}

      {reqActions?.isOpen && (
        <RequisitionsDetailsDrawer
          isOpen={reqActions?.isOpen}
          onClose={reqActions?.onClose}
          requisitionId={requisition?.id.toString()}
          readOnlyMode={!!readOnlyOnMobile}
          onActionComplete={() => {
            reqActions?.onClose();
          }}
        />
      )}
    </Flex>
  );

  return (
    <KPopover
      triggerElement={
        <Box mt="-3px">
          <Icon
            as={InfoCircle}
            width="16px"
            height="16px"
            cursor="pointer"
            onClick={readOnlyOnMobile ? onOpen : undefined}
            onMouseEnter={!readOnlyOnMobile ? onOpen : undefined}
          />
        </Box>
      }
      showArrow={false}
      showCloseButton={false}
      popoverProps={{
        trigger: readOnlyOnMobile ? 'click' : 'hover',
        placement: 'right',
      }}
      popoverContentProps={{
        maxW: '200px',
        boxShadow: 'none',
        ...(readOnlyOnMobile
          ? {}
          : { onMouseEnter: onOpen, onMouseLeave: onClose }),
      }}
      popoverActions={{ isOpen, onOpen, onClose }}
    >
      {requisitionDetails}
    </KPopover>
  );
};
